import { formatPoints } from "@iec-id/shared";
import { AccountMenuPopover, AvatarIcon, cn, Icon, initialsOf, StatusBadge } from "@iec-id/ui";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/trpc";

export type AccountButtonProps = {
	/** `full` — шапка десктопа (аватар, имя, уровень), `compact` — мобильная. */
	variant?: "full" | "compact";
	onSignOut: () => void;
};

/**
 * Блок пользователя в шапке и меню аккаунта (макеты 2a и 3g).
 *
 * Меню — тот же `AccountMenu` из `@iec-id/ui`, который встраивается в чужие
 * сервисы: витрина не рисует свою копию, иначе баланс и уровень начали бы
 * расходиться между экосистемой и личным кабинетом.
 */
export function AccountButton({ variant = "full", onSignOut }: AccountButtonProps) {
	const { t, i18n } = useTranslation("common");
	const navigate = useNavigate();
	// Открытость меню — состояние ЭКЗЕМПЛЯРА, не оболочки: шапки десктопа и
	// мобильного смонтированы обе (спрятана только CSS-ом), и общий флаг в сторе
	// открывал сразу два поповера. Второй перехватывал фокус у первого, слой
	// считал это «фокус ушёл наружу» и тут же закрывал оба — по клику на аватар
	// не открывалось ничего.
	const [open, setOpen] = useState(false);

	const me = api.auth.me.useQuery();
	const points = api.points.summary.useQuery();
	const entitlements = api.entitlements.mine.useQuery();
	// Пункт «Правовой центр» из навигации убран, поэтому единственное место, где
	// человек узнаёт о новой редакции до блокирующего экрана, — этот пункт меню.
	const pendingLegal = api.legal.pendingAcceptance.useQuery({});
	const pendingLegalCount = pendingLegal.data?.items.length ?? 0;

	const tier = entitlements.data?.effectiveTier ?? "free";
	// Уровень называется прямо — «Free», «IEC+», «IEC+ Pro». Раньше у free
	// подпись всё равно читалась «IEC+» (ключ `tier.free` хранил это значение),
	// то есть шапка сообщала о подписке, которой нет.
	const planLabel = t(`tier.${tier}`);
	const name = me.data?.displayName ?? "";
	const balance = points.data ? formatPoints(points.data.balance, i18n.language) : undefined;

	const trigger =
		variant === "compact" ? (
			<button
				type="button"
				aria-label={t("header.account")}
				aria-haspopup="menu"
				aria-expanded={open}
				className="iec-press-sm cursor-pointer rounded-full focus-ring"
			>
				<AvatarIcon
					size={40}
					{...(me.data?.avatarUrl ? { src: me.data.avatarUrl } : {})}
					alt={name}
					initials={initialsOf(name)}
				/>
			</button>
		) : (
			<button
				type="button"
				aria-label={t("header.account")}
				aria-haspopup="menu"
				aria-expanded={open}
				className={cn(
					"iec-press-glyph flex h-12 cursor-pointer items-center gap-2.5 rounded-control py-0 pr-2 pl-1",
					"transition-colors duration-150 ease-smooth hover:bg-sunken",
					"focus-ring",
				)}
			>
				<AvatarIcon
					size={40}
					{...(me.data?.avatarUrl ? { src: me.data.avatarUrl } : {})}
					alt={name}
					initials={initialsOf(name)}
				/>
				<span className="flex flex-col items-start gap-px">
					<span className="t-row">{name}</span>
					<span className="text-caps-sm text-fg-muted">{planLabel}</span>
				</span>
				<Icon name="ChevronDown" className="text-fg-muted" />
			</button>
		);

	return (
		<AccountMenuPopover
			trigger={trigger}
			open={open}
			onOpenChange={setOpen}
			user={{
				name,
				email: me.data?.email ?? "",
				...(me.data?.avatarUrl ? { avatarUrl: me.data.avatarUrl } : {}),
				initials: initialsOf(name),
			}}
			{...(balance !== undefined ? { pointsBalance: balance } : {})}
			{...(planLabel !== undefined ? { plan: planLabel } : {})}
			onManage={() => {
				setOpen(false);
				void navigate({ to: "/personal" });
			}}
			items={[
				{
					key: "points",
					label: t("accountMenu.pointsAndInvites"),
					icon: <Icon name="Coins" />,
					onSelect: () => {
						setOpen(false);
						void navigate({ to: "/points" });
					},
				},
				{
					key: "security",
					label: t("accountMenu.security"),
					icon: <Icon name="ShieldCheck" />,
					onSelect: () => {
						setOpen(false);
						void navigate({ to: "/security" });
					},
				},
				{
					// «Помощь» перед документами: короткие ответы про аккаунт нужны
					// чаще, чем юридические тексты (замечание владельца 22.08.2026).
					key: "faq",
					label: t("accountMenu.help"),
					icon: <Icon name="CircleHelp" />,
					onSelect: () => {
						setOpen(false);
						void navigate({ to: "/faq" });
					},
				},
				{
					key: "legal",
					label: t("accountMenu.documents"),
					icon: <Icon name="Scroll" />,
					...(pendingLegalCount > 0
						? {
								trailing: (
									<StatusBadge
										progress="todo"
										aria-label={t("accountMenu.pendingDocuments", { count: pendingLegalCount })}
									>
										{pendingLegalCount}
									</StatusBadge>
								),
							}
						: {}),
					onSelect: () => {
						setOpen(false);
						void navigate({ to: "/legal" });
					},
				},
			]}
			onSignOut={() => {
				setOpen(false);
				onSignOut();
			}}
			labels={{
				manage: t("accountMenu.manage"),
				signOut: t("accountMenu.signOut"),
				pointsUnit: t("accountMenu.pointsUnit"),
				menu: t("header.account"),
			}}
		/>
	);
}
